import { Link } from 'react-router-dom';
import { SEOHead } from '../components/seo/SEOHead';
import { Button } from '../components/ui/Button';
import { SealBadge } from '../components/ui/SealBadge';
import { buildBreadcrumbSchema } from '../lib/seo-utils';

const VALUES = [
  {
    title: 'Local Expertise',
    desc: 'We are based in Purulia and understand the local business landscape, government offices, and the specific challenges businesses in West Bengal face — not a generic, one-size-fits-all service.',
    accent: 'bg-seal-blue/10',
  },
  {
    title: 'Clear, Personalized Pricing',
    desc: 'Every quote is discussed with you directly, with a clear tax breakdown, so you know exactly what you\u2019re paying for before you commit to anything.',
    accent: 'bg-marigold/15',
  },
  {
    title: 'Genuine Follow-Through',
    desc: 'We don\u2019t disappear after you pay. Our team follows your application through to completion and keeps you updated at every stage.',
    accent: 'bg-ledger-green/10',
  },
  {
    title: 'No-Pressure Consultations',
    desc: 'If you\u2019re unsure what you need, talk to us first. We\u2019d rather you make an informed decision than rush into a service that isn\u2019t right for you.',
    accent: 'bg-gold/15',
  },
];

export default function About() {
  return (
    <>
      <SEOHead
        title="About Bharat Consultancy — Tax & Accounting Professional in Purulia"
        description="Learn about Bharat Consultancy, a trusted tax and accounting firm serving Purulia and West Bengal with GST, MSME, income tax, and registration services."
        canonicalPath="/about"
        jsonLd={buildBreadcrumbSchema([
          { name: 'Home', url: 'https://bharat-consultancy.com/' },
          { name: 'About Us', url: 'https://bharat-consultancy.com/about' },
        ])}
      />

      <section className="border-b border-line bg-gradient-to-br from-paper via-paper to-marigold/5 py-14">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <p className="font-mono text-xs uppercase tracking-widest text-ledger-green">About Us</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-ink sm:text-4xl">
            India&apos;s Trusted Tax, Legal &amp; Business Compliance Partner
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-ink/70">
            Bharat Consultancy is one of India&apos;s emerging Tax, Accounting, Business Registration and
            Compliance Consultancy firms. We provide professional services for startups, entrepreneurs, MSMEs,
            companies, professionals and individuals across India.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl space-y-16 px-4 py-14 sm:px-6">
        <section className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold text-ink">Our Story</h2>
            <p className="mt-4 leading-relaxed text-ink/75">
              Bharat Consultancy started in Munsifdanga, Purulia, helping local shop owners and small businesses
              navigate GST registration when the rules were still new and confusing for most people. What began as
              informal help for neighbors and friends has grown into a full-service consultancy serving startups,
              entrepreneurs, MSMEs, companies, and professionals across India.
            </p>
            <p className="mt-4 leading-relaxed text-ink/75">
              Our mission is to simplify taxation, legal documentation, business registration, and statutory
              compliance through technology and professional expertise — whether you're starting a new business,
              filing taxes, registering GST, obtaining licenses, or preparing project reports, we provide complete
              assistance under one roof.
            </p>
          </div>
          <div className="flex justify-center">
            <SealBadge size="lg">
              <span className="block text-lg">✓</span>
              Filed &amp;
              <br />
              Approved
            </SealBadge>
          </div>
        </section>

        <section>
          <h2 className="text-center font-display text-2xl font-bold text-ink">What We Stand For</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {VALUES.map((value) => (
              <div key={value.title} className="rounded-lg border border-line bg-white p-6">
                <div className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${value.accent}`}>
                  <span className="text-lg">★</span>
                </div>
                <h3 className="mt-3 font-display text-lg font-semibold text-ink">{value.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/65">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-line bg-white p-8 text-center">
          <h2 className="font-display text-xl font-bold text-ink">Where to Find Us</h2>
          <address className="mt-3 not-italic leading-relaxed text-ink/70">
            2nd Floor, Aradhya Complex, SN Sarkar St,
            <br />
            Munsifdanga, Purulia, West Bengal 723101
          </address>
          <p className="mt-3 text-ink/70">
            Phone/WhatsApp:{' '}
            <a href="tel:+918170896296" className="font-medium text-seal-blue">
              81708 96296
            </a>
          </p>
        </section>

        <section className="flex flex-col items-center gap-4 rounded-lg border border-line bg-white p-8 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <h2 className="font-display text-xl font-bold text-ink">Ready to get started?</h2>
            <p className="mt-1 text-sm text-ink/60">Browse our services or talk to a consultant first — no pressure.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/consult">
              <Button variant="accent">Talk to a Consultant</Button>
            </Link>
            <Link to="/services">
              <Button>Browse Services</Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
